import { REWARDS_ART } from '@/assets/rewards';

/**
 * Visual constants shared by the Rewards Hub blocks, transcribed from the Figma
 * frames. Gradients are written as CSS so a block never has to inline the SVG
 * paint Figma exports.
 */

/**
 * The page column Figma lays the hub and the leaderboard in: 1320px, the app
 * layout's own 60px gutters and no second inset inside them (29.09).
 * `data-rewards-surface` turns the page surface pure black (globals.css).
 */
export const REWARDS_PAGE_CLASS = 'mx-auto max-w-[1320px] px-0 sm:px-0 md:px-0';

/** The gold coin an album's reward line wears. */
export const ALBUM_REWARD_COIN = REWARDS_ART['coin.webp'];

/** "Album Reward:" — cold silver, lit at both ends (Figma 13449:475954). */
export const ALBUM_REWARD_LABEL_GRADIENT =
  'linear-gradient(to top, #cee2ff 15.625%, #939393 30.87%, #cfe2ff 65.625%)';

/**
 * Brushed-silver texture Figma paints on every block title (Title/M Bold 24/32,
 * node 12426:314846): white top, grey band around 70%, lighter foot.
 */
export const TITLE_GRADIENT =
  'linear-gradient(180deg, #ffffff 0%, #ffffff 30%, #b2b2b2 68%, #b2b2b2 71%, #d2d0d0 74%, #d2d0d0 100%)';

/** Bonus caption: white with a grey band low down, per Figma's text fill. */
export const BONUS_GRADIENT =
  'linear-gradient(180deg, #ffffff 20.6%, #b1b1b1 76.3%, #f5f5f5 85.3%)';

export const SECTION_TITLE_CLASS =
  'text-[24px] font-semibold leading-8 text-transparent bg-clip-text';

/** "Stay in the Game": warm glow rising from the bottom centre. */
export const DAILY_PANEL_BACKGROUND =
  'radial-gradient(86% 82% at 56% 95%, #332400 0%, #221800 17%, #110c00 58%, #090600 79%, #000000 100%)';

/** "Climb the Ranks": cold glow falling from the top centre. */
export const RANKS_PANEL_BACKGROUND =
  'radial-gradient(44% 100% at 45% 0%, #021a40 0%, #010d20 50%, #010610 75%, #000000 100%)';

/**
 * "Your Quests" (Figma 12500:150181): the ranks panel's cold light falling from
 * the top centre, sized in px — the panel grows with its groups and the glow
 * must not stretch with it — on black, in a #01386f frame.
 */
export const QUESTS_PANEL_BACKGROUND =
  'radial-gradient(809px 336px at 49% 0%, #021a40 0%, #010d20 50%, #010610 75%, #000000 100%)';
export const QUESTS_PANEL_BORDER = '#01386f';
/** The frame itself runs navy → violet → ice → navy (Ready to Dev 13449:471883). */
export const QUESTS_PANEL_EDGE =
  'linear-gradient(135deg, #01386f 0%, #3b00a1 29%, #a8dcff 76%, #00357f 100%)';

/** Ellipse 27262: a 1059px navy pool sunk into the panel's bottom-right corner. */
export const QUESTS_PANEL_POOL =
  'radial-gradient(50% 50% at 50% 50%, #003063 0%, rgba(0,48,99,0) 100%)';

/**
 * Diagonal light streak Figma lays over each daily card. The band is far larger
 * than the card and is clipped by it, which is what gives the off-centre sweep.
 */
export const CARD_SHEEN =
  'linear-gradient(-88.03deg, #d9d9d9 1.98%, rgba(217, 217, 217, 0) 96.98%)';

/**
 * The page title of a Rewards Hub hero (Figma 12500:152232 and 13449:466466
 * both paint it): white down to a champagne band, then white again, with a
 * faint glow behind the letters.
 */
export const HERO_TITLE_GRADIENT =
  'linear-gradient(179.7deg, #ffffff 26.5%, #a89175 70.8%, #ffffff 91.7%)';

/** The filigree rule Figma hangs under a hero title. */
export const HERO_TITLE_LINE = REWARDS_ART['hero-title-line.webp'];

/**
 * The gold floor under a hero band (Figma Ellipse 27247): a 1440×100 ellipse
 * of #956000 at 74% fading out, centred 8px above the band's bottom edge and
 * laid BEHIND the black band — so what shows is its lower 42px, spilling out
 * under the band rather than lighting a strip inside it. Sized in % of the
 * band's width so it keeps the proportion below 1440.
 */
export const STAGE_FLOOR_HEIGHT = 42;
export const STAGE_FLOOR =
  'radial-gradient(50% 50px at 50% -8px, rgba(149,96,0,0.74) 0%, rgba(149,96,0,0) 100%)';

/**
 * Warm haze over the hero chest (Figma Ellipse 27246): an 825×399 ellipse of
 * #ae8500 fading to transparent #ffc507, tilted ~20°, at 18%.
 */
export const CHEST_HAZE =
  'radial-gradient(50% 50% at 50% 50%, rgba(174,133,0,1) 0%, rgba(255,197,7,0) 100%)';
